import type { Locale } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';

type FormatLanguage = {
  value: string;
  label: string;
  dateFnsKey: string;
};

/**
 * Languages supported by both date-fns (frontend) and babel (backend).
 * One entry per language, sorted alphabetically by label.
 *
 * - `value`: stored in the database (babel-compatible locale code)
 * - `label`: display name in the language picker
 * - `dateFnsKey`: key in `date-fns/locale` exports
 */
const LANGUAGES: FormatLanguage[] = [
  { value: 'af', label: 'Afrikaans', dateFnsKey: 'af' },
  { value: 'sq', label: 'Albanian', dateFnsKey: 'sq' },
  { value: 'ar', label: 'Arabic', dateFnsKey: 'ar' },
  { value: 'hy', label: 'Armenian', dateFnsKey: 'hy' },
  { value: 'az', label: 'Azerbaijani', dateFnsKey: 'az' },
  { value: 'bn', label: 'Bangla', dateFnsKey: 'bn' },
  { value: 'eu', label: 'Basque', dateFnsKey: 'eu' },
  { value: 'be', label: 'Belarusian', dateFnsKey: 'be' },
  { value: 'bs', label: 'Bosnian', dateFnsKey: 'bs' },
  { value: 'bg', label: 'Bulgarian', dateFnsKey: 'bg' },
  { value: 'ca', label: 'Catalan', dateFnsKey: 'ca' },
  { value: 'hr', label: 'Croatian', dateFnsKey: 'hr' },
  { value: 'cs', label: 'Czech', dateFnsKey: 'cs' },
  { value: 'da', label: 'Danish', dateFnsKey: 'da' },
  { value: 'nl', label: 'Dutch', dateFnsKey: 'nl' },
  { value: 'en_US', label: 'English', dateFnsKey: 'enUS' },
  { value: 'eo', label: 'Esperanto', dateFnsKey: 'eo' },
  { value: 'et', label: 'Estonian', dateFnsKey: 'et' },
  { value: 'fi', label: 'Finnish', dateFnsKey: 'fi' },
  { value: 'fr', label: 'French', dateFnsKey: 'fr' },
  { value: 'fy', label: 'Western Frisian', dateFnsKey: 'fy' },
  { value: 'gd', label: 'Scottish Gaelic', dateFnsKey: 'gd' },
  { value: 'gl', label: 'Galician', dateFnsKey: 'gl' },
  { value: 'ka', label: 'Georgian', dateFnsKey: 'ka' },
  { value: 'de', label: 'German', dateFnsKey: 'de' },
  { value: 'el', label: 'Greek', dateFnsKey: 'el' },
  { value: 'gu', label: 'Gujarati', dateFnsKey: 'gu' },
  { value: 'ht', label: 'Haitian Creole', dateFnsKey: 'ht' },
  { value: 'he', label: 'Hebrew', dateFnsKey: 'he' },
  { value: 'hi', label: 'Hindi', dateFnsKey: 'hi' },
  { value: 'hu', label: 'Hungarian', dateFnsKey: 'hu' },
  { value: 'is', label: 'Icelandic', dateFnsKey: 'is' },
  { value: 'id', label: 'Indonesian', dateFnsKey: 'id' },
  { value: 'it', label: 'Italian', dateFnsKey: 'it' },
  { value: 'ja', label: 'Japanese', dateFnsKey: 'ja' },
  { value: 'kn', label: 'Kannada', dateFnsKey: 'kn' },
  { value: 'kk', label: 'Kazakh', dateFnsKey: 'kk' },
  { value: 'km', label: 'Khmer', dateFnsKey: 'km' },
  { value: 'ko', label: 'Korean', dateFnsKey: 'ko' },
  { value: 'lv', label: 'Latvian', dateFnsKey: 'lv' },
  { value: 'lt', label: 'Lithuanian', dateFnsKey: 'lt' },
  { value: 'lb', label: 'Luxembourgish', dateFnsKey: 'lb' },
  { value: 'mk', label: 'Macedonian', dateFnsKey: 'mk' },
  { value: 'ms', label: 'Malay', dateFnsKey: 'ms' },
  { value: 'mt', label: 'Maltese', dateFnsKey: 'mt' },
  { value: 'mn', label: 'Mongolian', dateFnsKey: 'mn' },
  { value: 'nb', label: 'Norwegian Bokmål', dateFnsKey: 'nb' },
  { value: 'nn', label: 'Norwegian Nynorsk', dateFnsKey: 'nn' },
  { value: 'oc', label: 'Occitan', dateFnsKey: 'oc' },
  { value: 'pl', label: 'Polish', dateFnsKey: 'pl' },
  { value: 'pt', label: 'Portuguese', dateFnsKey: 'pt' },
  { value: 'ro', label: 'Romanian', dateFnsKey: 'ro' },
  { value: 'ru', label: 'Russian', dateFnsKey: 'ru' },
  { value: 'sr', label: 'Serbian', dateFnsKey: 'sr' },
  { value: 'sk', label: 'Slovak', dateFnsKey: 'sk' },
  { value: 'sl', label: 'Slovenian', dateFnsKey: 'sl' },
  { value: 'es', label: 'Spanish', dateFnsKey: 'es' },
  { value: 'sv', label: 'Swedish', dateFnsKey: 'sv' },
  { value: 'ta', label: 'Tamil', dateFnsKey: 'ta' },
  { value: 'te', label: 'Telugu', dateFnsKey: 'te' },
  { value: 'th', label: 'Thai', dateFnsKey: 'th' },
  { value: 'tr', label: 'Turkish', dateFnsKey: 'tr' },
  { value: 'ug', label: 'Uyghur', dateFnsKey: 'ug' },
  { value: 'uk', label: 'Ukrainian', dateFnsKey: 'uk' },
  { value: 'uz', label: 'Uzbek', dateFnsKey: 'uz' },
  { value: 'vi', label: 'Vietnamese', dateFnsKey: 'vi' },
];

export const FORMAT_LANGUAGES: { value: string; label: string }[] = LANGUAGES;

const LOCALE_MAP: Record<string, Locale> = {};
for (const lang of LANGUAGES) {
  const locale = (dateFnsLocales as Record<string, Locale>)[lang.dateFnsKey];
  if (locale) {
    LOCALE_MAP[lang.value] = locale;
  }
}

export const getDateFnsLocale = (value: string): Locale | undefined => LOCALE_MAP[value];
